import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { firstValueFrom, from, Observable, Subject } from "rxjs";
import { AppDate } from "@app/util/app-date";
import { FuturePayment } from "@app/models/FuturePayment";
import { MatDialog } from "@angular/material/dialog";
import { PropertyApi } from "@app/services/apiServices/PropertyApi";
import { ContactApi } from "@app/services/apiServices/ContactApi";
import { PaymentMethodApi } from "@app/services/apiServices/PaymentMethodApi";
import { PaymentScheduleApi } from "@app/services/apiServices/PaymentScheduleApi";
import { SpinnerService } from "@app/services/spinner.service";
import { NetworkErrorService } from "@app/services/NetworkError.service";
import { AppDataService } from "@app/services/appData.service";
import { PaymentStore } from "@app/services/stores/PaymentStore";
import { WindowToken } from "@app/util/window.token";
import { PaymentApi } from "@app/services/apiServices/PaymentApi";
import {
    genericDateValidationMessage,
    PaymentDateService,
    PaymentDateValidation,
    PaymentDateValidationMessages
} from "@app/services/PaymentDate.service";
import { UserStore } from "@app/services/stores/UserStore";
import { AmenityApi } from "@app/services/apiServices/AmenityApi";
import { AmenityService } from "@app/services/Amenity.service";
import { ErrorHandlerService } from "@app/services/ErrorHandler.service";
import { PaymentUiState } from "@app/services/shared/PaymentUiState.enum";
import { Property } from "@app/models/Property";
import { Payment } from "@app/models/Payment";
import { PaymentMethod } from "@app/models/PaymentMethod";
import { PaymentRequest } from "@app/models/PaymentRequest";
import { PaymentRequestDetail } from "@app/models/PaymentRequestDetail";
import { Amenity } from "@app/models/Amenity";
import { AmenityNameCatalog } from "@app/models/AmenityNameCatalog";
import { PaymentSchedule } from "@app/models/PaymentSchedule";
import { FixedDayStartDate } from "@app/models/FixedDayStartDate";
import { PaymentChannel } from "@app/services/shared/PaymentChannel.enum";
import { map, take, takeUntil } from "rxjs/operators";
import { Product } from "@app/models/Product";
import { todo } from "@app/util/todo";
import { OneButtonDialogComponent } from "@app/components/sharedDialogs/oneButtonDialog.component";
import { DialogErrorDialog } from "@app/components/sharedDialogs/dialogError.dialog";
import { moneyAdd, moneyNumber } from "@app/util/money-number";
import { PaymentEntityType } from "@app/models/PaymentEntityType";
import { PaymentLineItem } from "@app/models/PaymentLineItem";
import { DateObject } from "@app/models/DateObject";
import { ScheduleOverlapDialog } from "@app/pages/payment/schedule-overlap.dialog";
import { DATE_PICKER_CONFIG } from "@app/components/material/matDatePicker/mat-date-picker.component";
import { PaymentTimingType } from "@app/services/shared/PaymentTimingType.enum";
import { ActivatedRoute, Router } from "@angular/router";
import { ScheduleFrequencyType } from "@app/services/shared/ScheduleFrequencyType.enum";
import { PaymentCorrelationRequest } from "@app/models/PaymentCorrelation";
import { PaymentCorrelationService } from "@app/services/payment-correlation.service";


/** Just for reference when calculating overlaps */
type PaymentScheduleRange = {
    startDate: AppDate,
    endDate: AppDate,
    nickname: string,
    futurePayment: FuturePayment,
}

const StatusType: any = {
    ACCOUNT_BALANCE: 'account-balance',
    FIXED_AMOUNT: 'fixed-amount'
};

@Component({
    selector: 'app-payment',
    template: require('./payment.component.html')
})
export class PaymentComponent implements OnInit, OnDestroy {

    @ViewChild('propertyPaymentForm', { static: true }) private propertyPaymentForm;
    // @ViewChild('selectAmenitiesCart') private selectAmenitiesCart: SelectAmenitiesCartComponent;

    /** used by template */
    UI = PaymentUiState

    /** the kind of payment we want to show */
    uiManipulation: PaymentUiState;

    property: Property;
    payment: Payment;
    paymentMethod: PaymentMethod;
    paymentRequest: PaymentRequest;
    paymentMethodError: boolean;
    calculatingConvenienceFee: boolean = false;
    paymentMethodErrorMsg: string;

    /** created outside of constructor to allow for better testing instead of just making public */
    //PaymentStore: PaymentStore;

    totalAmount: number;
    amountToPay: number;
    convenienceFee: number;
    dueDate: any;
    accountBalanceAdminFee: number = 0;
    amountType: string;
    amountToPayInputValue: string;
    cartItems: PaymentRequestDetail[] = [];
    availableAmenities: Amenity[];
    availableCategories: AmenityNameCatalog[];

    maxPrincipalAmount: number;

    // @ts-ignore
    MAX_PAYMENTS = todo("Do we need to make the limit of payments infinite? ISBMDPDPP-3111") || 18

    paymentMethods: PaymentMethod[];
    /** ng-model for select of paymentMethods */
    currentPaymentMethod: PaymentMethod;

    /** Schedule payment; is also used for future dated one-time payments */
    paymentScheduleRequest: PaymentSchedule;

    startDate: AppDate = null;
    startDateError: string = null;
    endDate: AppDate = null;
    endDateError: string = null;

    /** config for date-picker, must be set in constructor! */
    startDatePickerConfig: DATE_PICKER_CONFIG = null;
    /** config for date-picker, must be set in constructor! */
    endDatePickerConfig: DATE_PICKER_CONFIG = null

    /** tied to the ui input */
    numberOfPaymentsInputValue: string = "1";
    /** 0 if invalid */
    numberOfPayments: number = 1;

    scheduleName: string = '';
    scheduleNameError: boolean = false;
    scheduleFrequencies: string[] = [];
    scheduleFrequency: string;
    selectEndDate: "No End Date" | "Enter Number of Payments" = "No End Date";
    currentPaymentScheduleType: string = StatusType.FIXED_AMOUNT;
    paymentScheduleOptions: any;
    scheduleFixedDayStartDates?: FixedDayStartDate[];
    scheduleFixedDayStartDate?: FixedDayStartDate;
    scheduleFixedDay?: number;

    futurePayment: FuturePayment[];
    scheduleRanges: {
        [key: string]: PaymentScheduleRange
    };

    ACHMessage: string;
    cardMessage: string;
    amountToPayErrorMsg: string;
    amenityTotalErrorMsg: string;
    baseStateUrl: string;

    /** amount due provided from billing summary */
    amountDue?: number = null
    /** show "Reset Original Amount" link or not */
    showResetAmountLink: boolean = false

    allowPayByCheck: boolean = true;
    allowPayByCard: boolean = true;

    $stateParams: any;
    private paymentChannel: PaymentChannel = PaymentChannel.WEB
    private destroy$ = new Subject<void>();

    constructor(
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(PropertyApi) private propertyApi: PropertyApi,
        @Inject(ContactApi) private contactApi: ContactApi,
        @Inject(PaymentMethodApi) private paymentMethodApi: PaymentMethodApi,
        @Inject(PaymentScheduleApi) private paymentScheduleApi: PaymentScheduleApi,
        @Inject(PaymentCorrelationService) private paymentCorrelationService: PaymentCorrelationService,
        @Inject(SpinnerService) private spinnerService: SpinnerService,
        @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
        @Inject(AppDataService) private appDataService: AppDataService,
        @Inject(PaymentStore) private paymentStore: PaymentStore,
        @Inject(WindowToken) private $window: Window,
        @Inject(PaymentApi) private paymentApi: PaymentApi,
        @Inject(PaymentDateService) private paymentDateService: PaymentDateService,
        @Inject(UserStore) private userStore: UserStore,
        @Inject(AmenityApi) private amenityApi: AmenityApi,
        @Inject(AmenityService) private amenityService: AmenityService,
        @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
        @Inject(Router) private router: Router,
        @Inject(ActivatedRoute) private route: ActivatedRoute) {

        if(this.router.getCurrentNavigation()) {
            this.$stateParams = this.router.getCurrentNavigation().extras.state;
        }

    }

    ngOnInit() {

        this.uiManipulation = this.paymentStore.getUiState();

        if (this.$stateParams && this.$stateParams.property) {
            this.property = this.$stateParams.property;
            this.paymentStore.setProperty(this.property);
        } else if (this.paymentStore.getProperty()) {
            this.property = this.paymentStore.getProperty();
        } else {
            this.router.navigateByUrl('/properties')
        }

        if (this.$stateParams && this.$stateParams.availableAmenities) {
            this.availableAmenities = this.$stateParams.availableAmenities;
            this.paymentStore.setAvailableAmenities(this.availableAmenities);
        } else if (this.paymentStore.getAvailableAmenities()) {
            this.availableAmenities = this.paymentStore.getAvailableAmenities();
        }

        if (this.property && this.property.nextPaymentDueDate) {
            this.dueDate = this.property.nextPaymentDueDate.formattedFullDate;
        }

        this.convenienceFee = this.amountToPay = 0;
        this.ACHMessage = "Please note: you are making a payment through First Citizens Bank’s proprietary ACH payment system.";

        if (this.uiManipulation === PaymentUiState.SCHEDULE_PAYMENT) {
            this.scheduleName = this.paymentStore.getScheduleName();
            this.setNumberOfPayments(this.paymentStore.getNumberOfPayments());
            this.scheduleFrequency = this.paymentStore.getScheduleFrequency();
            if (this.paymentStore.getPaymentScheduleRequest() != null &&
                this.paymentStore.getPaymentScheduleRequest().endDate != null) {
                this.selectEndDate = "Enter Number of Payments";
            }
        }

        if (this.uiManipulation === PaymentUiState.AMENITY_PAYMENT) {
            if (this.paymentStore.getCartItems()) {
                this.cartItems = this.paymentStore.getCartItems();
                this.validateCartItems();
                this.updateCartItemsTotal();
            }
            this.updateAmount();
        }

        this.setPaymentOptions();
        this.refreshAmountDue();

        this.validateProperty().then(() => {
            this.getPaymentMethods();
            this.setDefaultDate();
        });

        this.getScheduleRanges();
        this.setPaymentScheduleOptions();
        this.maxPrincipalAmount = this.appDataService.appSettings.paymentLimits.maxPrincipalAmount;
    }

    setAmountToPayInputValue(value?: number) {
        const amt = moneyNumber(value)
        if (value == null) {
            this.amountToPayInputValue = ''
        } else if (!isNaN(amt)) {
            this.amountToPayInputValue = amt.toFixed(2)
        } else {
            console.error('setAmountToPay to illegal value', value)
        }

        this.updateAmount()
    }

    validateProperty(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.propertyApi.propertiesValidateGet(
                false,
                this.property.hoa.pmc.couponId,
                this.property.hoa.couponId,
                this.property.couponId
            ).pipe(take(1)).subscribe((validateResp) => {
                this.setAllowCheckAndCard(validateResp.achPayments, validateResp.cardPayments);
                this.paymentDateService.delayTodaysPayment = validateResp.delayPayment;
                resolve();
            }, () => {
                resolve();
            });
        });
    }

    setAllowCheckAndCard(allowPayByCheck: boolean, allowPayByCard: boolean) {
        this.allowPayByCheck = allowPayByCheck;
        this.allowPayByCard = allowPayByCard;
    }

    saveView() {
        this.paymentStore.setUiState(this.uiManipulation);

        if (this.isFixedDaySchedule())
            this.updateScheduleFixedDayStartDate();
        else
            this.refreshDatePickers()
        // refresh to account for different cutoff hours

        if (this.uiManipulation === PaymentUiState.AMENITY_PAYMENT) {
            this.updateCartItemsTotal();
        }
        this.setDefaultDate();
        this.updateConvenienceFee(800);
    }

    updateAmount() {
        // this.selectedDues = [];
        this.amountToPay = Number(this.amountToPayInputValue);
        this.paymentStore.setAmountToPay(this.amountToPay)

        if (this.currentPaymentScheduleType === StatusType.ACCOUNT_BALANCE) {
            this.amountToPayErrorMsg = undefined;
        } else if (isNaN(this.amountToPay) || this.amountToPay === 0) {
            this.amountToPayErrorMsg = 'Please enter an amount.';

        } else if (this.maxPrincipalAmount && this.amountToPay > this.maxPrincipalAmount) {
            this.amountToPayErrorMsg = 'Payment amount cannot exceed $' + this.maxPrincipalAmount;

        } else {
            this.amountToPayErrorMsg = undefined;
        }

        this.updateConvenienceFee(400)
        this.updateResetOriginalAmountLink()
    }

    updateConvenienceFeeTimer: number;
    updateConvenienceFee(delay: number = 800) {
        // use cancel to debounce
        clearTimeout(this.updateConvenienceFeeTimer)
        this.calculatingConvenienceFee = true;

        // manually invoke a delay here so the convenience fee formula is not so obvious
        // @ts-ignore
        this.updateConvenienceFeeTimer = setTimeout(() => {
            if (!this.currentPaymentMethod) {
                this.currentPaymentMethod = this.paymentStore.getPaymentMethod();
            }
            let paymentMethodType: "ACH" | "CARD"
            if (this.currentPaymentMethod) {
                const isACH = this.currentPaymentMethod.routingNumber != null
                paymentMethodType = isACH ? "ACH" : "CARD";
            }
            if (this.property && this.currentPaymentMethod && this.amountToPay > 0) {

                const paymentMethodState = this.currentPaymentMethod.state
                const propertyState = this.property.address.state
                let  scheduleFreq =  this.scheduleFrequency;

                //  What should the state be?
                todo("paymentMethodState is undefined when using checking account, is this a bug? Defaulting to property state", { paymentMethodState, propertyState })

                let paymentTimingType = PaymentTimingType.ONE_TIME;
                if (this.uiManipulation === PaymentUiState.SCHEDULE_PAYMENT) {

                    paymentTimingType = PaymentTimingType.SCHEDULED;
                }
                if(this.uiManipulation === PaymentUiState.MAKE_PAYMENT || this.uiManipulation === PaymentUiState.AMENITY_PAYMENT) {
                    scheduleFreq = ScheduleFrequencyType.ONE_TIME;
                }

                this.paymentApi.convenienceFeePropertyIdAmountGet(
                    false,
                    this.property.address.state,
                    paymentMethodState || propertyState, // Is this a problem with checking?
                    this.amountToPay,
                    this.property.hoa.pmc.couponId,
                    this.property.hoa.couponId,
                    paymentMethodType,
                    paymentTimingType,
                    scheduleFreq).pipe(take(1)).subscribe((data) => {
                    this.calculatingConvenienceFee = false;
                    this.convenienceFee = data.convenienceFee;
                    this.totalAmount = moneyAdd(this.amountToPay, this.convenienceFee);
                }, () => {
                    this.calculatingConvenienceFee = false;
                });
            } else {
                this.convenienceFee = 0;
                this.totalAmount = 0;
                this.calculatingConvenienceFee = false;
            }
        }, delay)
    }

    async updateAccountBalanceAdminFee() {
        if (!this.currentPaymentMethod) {
            this.currentPaymentMethod = this.paymentStore.getPaymentMethod();
        }
        let paymentMethodType: "ACH" | "CARD"
        if (this.currentPaymentMethod) {
            const isACH = this.currentPaymentMethod.routingNumber != null
            paymentMethodType = isACH ? "ACH" : "CARD";
        }
        let paymentTimingType = PaymentTimingType.ONE_TIME;
        if (this.uiManipulation === PaymentUiState.SCHEDULE_PAYMENT) {
            paymentTimingType = PaymentTimingType.SCHEDULED;
        }
        if ((this.uiManipulation === PaymentUiState.SCHEDULE_PAYMENT) && (this.currentPaymentScheduleType === StatusType.ACCOUNT_BALANCE)) {
            const data = await firstValueFrom(this.paymentApi.getAdminFee(
                this.property.hoa.pmc.couponId,
                this.property.hoa.couponId,
                paymentMethodType,
                paymentTimingType,
                this.scheduleFrequency.toUpperCase()));
            if (!!data) {
                this.accountBalanceAdminFee = data.amount;
                this.amountType = data.type;
            }
        } else {
            this.accountBalanceAdminFee = 0;
        }
    }

    clearAmount() {
        this.setAmountToPayInputValue(0)
    }

    clearAmountFieldWhen0() {
        const amountToPay = Number(this.amountToPayInputValue)
        if (isNaN(amountToPay) || amountToPay <= 0) {
            this.setAmountToPayInputValue(null)
        }
    }

    resetPaymentAmount() {
        this.setAmountToPayInputValue(this.amountDue)
    }

    refreshAmountDue() {
        this.propertyApi.propertyAddressGet(
            false,
            this.property.hoa.pmc.couponId,
            this.property.hoa.couponId,
            this.property.couponId).pipe(take(1)).subscribe((addrResp) => {
            this.amountDue = Number(addrResp.amountDue) || null

            if (this.uiManipulation !== PaymentUiState.AMENITY_PAYMENT) {
                // check if a value is stored
                const storedAmtToPay = this.paymentStore.getAmountToPay()
                if (Number(storedAmtToPay) > 0) {
                    this.setAmountToPayInputValue(storedAmtToPay)

                } else if ((!(Number(this.amountToPayInputValue) > 0) && this.amountToPayInputValue) || this.amountDue) {
                    this.setAmountToPayInputValue(this.amountDue)
                }
            }
        })
    }

    getOriginalAmount(): number {
        // Baldwin: Treat redirect amount as an amount which is just filled in really fast (MMDP-2248)
        //          If there is no amount due from the billing summary, then there should not be any
        //          "Reset Original Amount" link
        return this.amountDue || undefined
    }

    updateResetOriginalAmountLink() {
        const paymentAmountNum = moneyNumber(this.amountToPayInputValue)

        // check to see if the link needs to be shown
        const originalAmount = this.getOriginalAmount();
        if (originalAmount == null) {
            // if there is no original amount, hide the link
            this.showResetAmountLink = false;
            return
        }

        this.showResetAmountLink = paymentAmountNum !== originalAmount;
    }

    isInvalidCart() {
        if (this.uiManipulation !== PaymentUiState.AMENITY_PAYMENT) return false
        return this.amenityService.invalid(this.cartItems);
    }

    isInvalidSchedule() {
        if (this.uiManipulation !== PaymentUiState.SCHEDULE_PAYMENT) return false

        const noEndDate = this.selectEndDate === 'No End Date' || this.endDate == null || this.endDateError
        const endDateIsFutureDate = this.endDate != null && !AppDate.now().isAfter(this.endDate)
        const numberOfPaymentsOutsideRange = this.numberOfPayments > this.MAX_PAYMENTS || this.numberOfPayments < 1

        return (
            !this.scheduleName //length empty, or too many payments
            || (!noEndDate && numberOfPaymentsOutsideRange)
            || (!noEndDate && !this.isEndDateAfterStartDate(this.endDate))
            || (!noEndDate && !endDateIsFutureDate)
            || (!noEndDate && !this.numberOfPayments) //both end date and number of payments is empty
        );
    }

    isEndDateAfterStartDate(endDate: AppDate) {
        if (endDate == null || this.startDate == null) return false
        return endDate.isAfter(this.startDate) || endDate.equals(this.startDate)
    }

    submitDisabled() {
        let zeroAmountDetected = false;

        if (this.currentPaymentScheduleType !== StatusType.ACCOUNT_BALANCE && ((Number(this.amountToPayInputValue) || 0) === 0)) {
            zeroAmountDetected = true;
        }

        const startDateIsNotPast = this.startDate && !this.startDate.isBefore(AppDate.now())

        return (
            zeroAmountDetected
            || this.startDateError
            || (this.uiManipulation === PaymentUiState.SCHEDULE_PAYMENT && this.endDateError)
            || (this.amountToPayErrorMsg && !(this.currentPaymentScheduleType === StatusType.ACCOUNT_BALANCE && this.uiManipulation === PaymentUiState.SCHEDULE_PAYMENT))
            || this.calculatingConvenienceFee
            || !startDateIsNotPast
            || (this.paymentMethods && this.paymentMethods.length === 0)
            || !this.currentPaymentMethod
            || this.isInvalidSchedule()
            || this.isInvalidCart()
        );
    }

    /** returns once everything is refreshed */
    setDefaultDate(): void {
        const forSchedule = this.uiManipulation === PaymentUiState.SCHEDULE_PAYMENT;

        (forSchedule ? this.getScheduledPaymentDate() : this.getOnetimePaymentDate()).pipe(
            takeUntil((this.destroy$))
        ).subscribe({
            next: (date) => this.applyDate(date),
            error: (err) => {
                const errMsg = err?.error?.message || undefined;
                this.errorHandlerService.handleNetworkError(err, true, errMsg);
            }
        });
    }

    private getScheduledPaymentDate(): Observable<AppDate> {
        const dateNewYorkTZ= new Date(new Date().toLocaleString('en-us', { timeZone: AppDate.timezone }));
        const startDate= AppDate.fromDate(dateNewYorkTZ);

        return from(this.paymentDateService.getNextValidPmtDate(startDate, true));
    }

    private getOnetimePaymentDate(): Observable<AppDate> {
        return this.paymentApi.getPaymentDate(this.paymentDateService.delayTodaysPayment, false).pipe(
            map(response => AppDate.fromString(response.paymentDate, 'yyyy-MM-dd'))
        );
    }

    private applyDate(date: AppDate): void {
        this.startDate = date;

        const endDate = this.paymentStore.getEndDate();
        this.setEndDate(endDate);

        if (endDate === null) {
            this.setNumberOfPayments(this.paymentStore.getNumberOfPayments());
        }

        this.refreshDatePickers();
    }

    async refreshDatePickers() {
        const forSchedule = this.uiManipulation === PaymentUiState.SCHEDULE_PAYMENT;
        // const startDate = this.startDate
        const endDate = this.endDate
        const cutoffHour = forSchedule ?
            this.appDataService.appConfig.CUTOFF_HOUR_SCHEDULE :
            this.appDataService.appConfig.CUTOFF_HOUR
        const cutoffMinute = forSchedule ?
            this.appDataService.appConfig.CUTOFF_MINUTE_SCHEDULE :
            this.appDataService.appConfig.CUTOFF_MINUTE

        const minDate = await this.paymentDateService.getNextValidPmtDate(AppDate.now(), forSchedule)

        // ensure date pickers are set up
        this.startDatePickerConfig = {
            defaultDate: this.startDate.isAfter(minDate) ? this.startDate : minDate,
            isDateEnabled: date => this.paymentDateService.validatePaymentDate(date, cutoffHour, cutoffMinute) === PaymentDateValidation.OK,
            minDate: minDate,
            validateDate: date => {
                return { value: date }
            },
            preloadYear: year => this.paymentDateService.updateHolidays([year]),
        }

        if (forSchedule) {
            // only start and end date pickers
            this.refreshEndDatePicker()

        } else {
            this.endDatePickerConfig = null
            this.endDateError = null
        }
    }

    getCutoffHour(): number {
        return this.uiManipulation === this.UI.SCHEDULE_PAYMENT ?
            this.appDataService.appConfig.CUTOFF_HOUR_SCHEDULE :
            this.appDataService.appConfig.CUTOFF_HOUR
    }

    getCutoffMinute(): number {
        return this.uiManipulation === this.UI.SCHEDULE_PAYMENT ?
            this.appDataService.appConfig.CUTOFF_MINUTE_SCHEDULE :
            this.appDataService.appConfig.CUTOFF_MINUTE
    }

    refreshEndDatePicker() {
        if (!this.isEndDateAfterStartDate(this.endDate)) this.endDate = this.startDate

        const minDate = this.startDate || AppDate.now()
        const endDate = this.endDate

        const cutoffHour = this.getCutoffHour()
        const cutoffMinute = this.getCutoffMinute()

        this.endDatePickerConfig = {
            defaultDate: endDate,
            isDateEnabled: date => this.paymentDateService.validatePaymentDate(date, cutoffHour, cutoffMinute) === PaymentDateValidation.OK,
            minDate: minDate,
            validateDate: date => {
                const pmtDateValidation = this.paymentDateService.validatePaymentDate(date, cutoffHour, cutoffMinute);
                if (pmtDateValidation === PaymentDateValidation.OK) return { value: date }

                const message = PaymentDateValidationMessages[pmtDateValidation] || genericDateValidationMessage

                return { error: message }
            },
            preloadYear: year => this.paymentDateService.updateHolidays([year]),
        }

    }

    filterAllowedPaymentMethods(paymentMethods: PaymentMethod[]): PaymentMethod[] {
        return paymentMethods.filter(paymentMethod => {
            if (paymentMethod.type === 'card')
                return this.allowPayByCard
            else if (paymentMethod.type === 'ach')
                return this.allowPayByCheck
            else
                return true;
        });
    }

    getPaymentMethods() {
        let userId = this.userStore.getHoaHomeownerId();
        this.spinnerService.showSpinner();
        this.paymentMethodApi.homeownersIdPaymentMethodsGet(userId)
            .pipe(take(1)).subscribe((resp) => {
                this.spinnerService.closeSpinner();
                this.paymentMethods = this.filterAllowedPaymentMethods(resp);
                this.setDefaultPaymentMethod();
                if (this.paymentMethods && this.paymentMethods.length === 0) {
                    this.paymentMethodErrorMsg = "Please add a payment method.";
                    this.paymentMethodError = true;
                } else {
                    this.paymentMethodErrorMsg = null;
                    this.paymentMethodError = false;
                }
            }, (err) => {
                this.networkErrorService.handleNetworkError(err, true)
                    .pipe(take(1)).subscribe((resp) => {
                        if (resp === true) {
                            this.getPaymentMethods();
                        }
                    });
            });
    }

    setDefaultPaymentMethod() {
        if (this.paymentStore.getPaymentMethod()) {
            let savedPaymentMethod = this.paymentStore.getPaymentMethod();
            for (let paymentMethod of this.paymentMethods) {
                if (paymentMethod && paymentMethod.id) {
                    if (paymentMethod.id === savedPaymentMethod.id) {
                        this.currentPaymentMethod = paymentMethod;
                        this.updatePaymentMethod();
                        break;
                    }
                }
            }
        }
        else if (this.paymentMethods) {
            this.currentPaymentMethod = "" as any; // TODO: fix type;; default to blank initially

            for (let p of this.paymentMethods) {
                if (p.defaultMethod === true) {
                    this.currentPaymentMethod = p;
                    this.updatePaymentMethod();
                    break;
                }
            }
        }
    }

    async affirmativeActionButton() {
        const userEmail = this.userStore.getEmail()

        if (
            this.uiManipulation === PaymentUiState.MAKE_PAYMENT ||
            this.uiManipulation === PaymentUiState.AMENITY_PAYMENT
        ) {
            this.createPaymentRequest();
            if (this.paymentRequest.futureDatedPayment) {
                const pmtDate = this.paymentRequest.paymentDate

                // handle fdp like a schedule payment
                this.setNumberOfPayments(1)
                this.scheduleFrequency = 'one-time'
                // check valid frequency strings https://webitg4.mutualofomaha.com/hoa-gateway/swagger-ui.html#/PaymentSchedule/paymentSchedulesIdAutocreateUsingPOST

                // per ISBMDPDPP-6341 "Updates to Future Dated Payments - Registered User",
                // Schedule Name is set to the Next Payment Date formatted as MM/DD/YYYY
                const mm = String(pmtDate.month).padStart(2, "0");
                const dd = String(pmtDate.day).padStart(2, "0");
                const yyyy = pmtDate.year;
                this.scheduleName = `${mm}/${dd}/${yyyy}`;

                this.checkForOverlap().then((result) => {
                    if (result.value === true) {
                        this.createPaymentScheduleRequest();
                        this.paymentStore.setPaymentScheduleRequest(this.paymentScheduleRequest);
                        this.paymentStore.setConvenienceFee(this.convenienceFee);
                        const navigationState = {
                            propertyId: this.paymentScheduleRequest.propertyId,
                            paymentScheduleRequest: this.paymentScheduleRequest,
                            property: this.property,
                            convenienceFee: this.convenienceFee
                        }

                        this.router.navigateByUrl('/properties/' + this.paymentRequest.property.id + '/futurePaymentConfirmation', {
                            state: navigationState,
                        });
                    }
                });
            } else {
                // not future dated
                this.paymentStore.clearCorrelationId();

                const correlationRequest: PaymentCorrelationRequest = {
                    payerId: this.userStore.getHoaHomeownerId(),
                    entityId: this.property.id.toString()
                }

                const correlationId = await firstValueFrom(
                    this.paymentCorrelationService.createCorrelationId(false, correlationRequest)
                        .pipe(take(1)));

                this.spinnerService.closeSpinner();

                if (!correlationId) {
                    return;
                }

                this.paymentStore.setCorrelationId(correlationId);

                todo("Remove dependency on paymentConfirmation stateparams in favor of paymentStore")
                this.paymentStore.setProperty(this.property);
                this.paymentStore.setPaymentRequest(this.paymentRequest);
                this.paymentStore.setCartItems(this.cartItems);

                const navigationState = {
                    propertyId: this.paymentRequest.property.id,
                    paymentRequest: this.paymentRequest,
                    email: userEmail,
                }

                await this.router.navigateByUrl('/properties/' + this.paymentRequest.property.id + '/paymentConfirmation', {
                    state: navigationState,
                });
            }
        }

        if (this.uiManipulation === PaymentUiState.SCHEDULE_PAYMENT) {
            await this.updateAccountBalanceAdminFee();
            this.checkForOverlap().then((result) => {
                if (result.value === true) {
                    this.createPaymentScheduleRequest();
                    this.paymentStore.setPaymentScheduleRequest(this.paymentScheduleRequest);
                    this.paymentStore.setConvenienceFee(this.convenienceFee);
                    this.router.navigateByUrl('/properties/' + this.paymentScheduleRequest.propertyId + '/scheduleConfirmation', {
                        state: {
                            propertyId: this.paymentScheduleRequest.propertyId,
                            paymentScheduleRequest: this.paymentScheduleRequest,
                            property: this.property,
                            convenienceFee: this.convenienceFee,
                            accountBalanceAdminFee: this.accountBalanceAdminFee,
                            amountType: this.amountType

                        }
                    });
                }
            });
        }
    }

    createPaymentRequest() {
        this.paymentMethod = {
            token: this.currentPaymentMethod.token,
            payerId: this.currentPaymentMethod.payerId,
            id: this.currentPaymentMethod.id,
            maskedId: this.currentPaymentMethod.maskedId,
            nickname: this.currentPaymentMethod.nickname,
            cardBrand: this.currentPaymentMethod.cardBrand || undefined,
            bankName: this.currentPaymentMethod.bankName || undefined,
            routingNumber: this.currentPaymentMethod.routingNumber || undefined,
        };

        // setting payment to null if account-balance payment schedule
        if (this.currentPaymentScheduleType === StatusType.ACCOUNT_BALANCE) {
            this.payment = null;
        } else {
            this.payment = {
                feeAmount: Number(this.convenienceFee),
                totalPaymentAmount: Number(this.totalAmount.toFixed(2)),
                principalAmount: Number((this.totalAmount - this.convenienceFee).toFixed(2)),
            };
        }

        // Logic required to determine futureDatedPayment requests
        const today = AppDate.now()
        const pmtDate = this.startDate
        const nextPmtDate =
            this.paymentDateService.getNextValidDate(
                today,
                this.appDataService.appConfig.CUTOFF_HOUR,
                this.appDataService.appConfig.CUTOFF_MINUTE,
                today.addDays(365))
        const isFutureDatedPayment = pmtDate.isAfter(nextPmtDate)
        const dObjStartDate = this.paymentDateService.dateObjectFromAppDate(this.startDate)

        this.paymentRequest = {

            // Current implementation is that effectiveDate = paymentDate
            // since the backend will 400 if effectivedate is in future and futureDatedPayment = false
            paymentDate: dObjStartDate,

            // effective date represents the date it will be processed.
            // MMDP-1890
            effectiveDate: dObjStartDate,

            // if this payment date is after the date which would typically process
            futureDatedPayment: isFutureDatedPayment,

            payment: this.payment,
            paymentMethod: this.paymentMethod,
            paymentChannel: this.paymentChannel,
            property: this.property,
            paymentSiteId: this.property.hoa.paymentSiteId,
            acceptedTerms: true, // TODO: make dynamic (does another process count as accepting?)
            acceptedTermsDate: AppDate.now().format("yyyy-MM-dd"), // TODO: make dynamic
            paymentEntityType: this.uiManipulation === PaymentUiState.AMENITY_PAYMENT  ? PaymentEntityType.AMENITY : PaymentEntityType.PROPERTY,
            details: this.uiManipulation === PaymentUiState.AMENITY_PAYMENT ? this.cartItems : null
        };
    }

    createPaymentScheduleRequest() {
        let futurePaymentItems: PaymentLineItem[] = null;
        let total = null;
        let nonAdjustedStartDate: DateObject = null;
        let fixedDay: number = null;

        if (this.currentPaymentScheduleType !== StatusType.ACCOUNT_BALANCE) {
            futurePaymentItems = [{
                description: "dues",
                amount: moneyNumber(this.amountToPay, 2),
            }, {
                description: "fee",
                amount: moneyNumber(this.convenienceFee, 2)
            }];

            total = moneyNumber(this.totalAmount, 2);
            const totalAccordingToItems = moneyAdd(...futurePaymentItems.map(item => item.amount))
            if (total !== totalAccordingToItems) {
                throw new Error("Failed to balance fee and dues with shown total!")
            }
        }

        if (this.isFixedDaySchedule()) {
            nonAdjustedStartDate = this.scheduleFixedDayStartDate.nonAdjustedStartDate;
            fixedDay = this.scheduleFixedDay;
        }

        this.futurePayment = [{
            date: this.paymentDateService.dateObjectFromAppDate(this.startDate),
            total: total,
            items: futurePaymentItems,
        }];

        this.paymentMethod = {
            payerId: this.currentPaymentMethod.payerId,
            id: this.currentPaymentMethod.id,
            maskedId: this.currentPaymentMethod.maskedId,
            nickname: this.currentPaymentMethod.nickname,
            cardBrand: this.currentPaymentMethod.cardBrand || undefined,
        };

        this.paymentScheduleRequest = {
            payerId: this.currentPaymentMethod.payerId,
            propertyId: this.property.id,
            frequency: this.scheduleFrequency.toUpperCase(), // monthly, quarterly, semi-annual, annual
            nickname: this.scheduleName,
            startDate: this.paymentDateService.dateObjectFromAppDate(this.startDate),
            nonAdjustedStartDate: nonAdjustedStartDate,
            fixedDay: fixedDay,
            endDate: this.selectEndDate === "Enter Number of Payments"
                ? this.paymentDateService.dateObjectFromAppDate(this.endDate)
                : this.paymentDateService.dateObjectFromAppDate(null),
            remainingPayments: this.selectEndDate === "Enter Number of Payments" ? Number(this.numberOfPayments) : null,
            futurePayments: this.futurePayment,
            acceptedAmounts: true,
            acceptedAmountsDate: null,
            paymentMethodType: (this.currentPaymentMethod.routingNumber) ? "ACH" : "CARD",
            paymentMethodId: this.currentPaymentMethod.id,
            paymentMethod: this.paymentMethod,
            paymentSiteId: this.property.hoa.paymentSiteId,
            type: this.currentPaymentScheduleType,
            channel: this.paymentChannel
        };

        try {
            this.startDateError = undefined
            this.endDateError = undefined
        } catch (e) {
            console.error(e);
            //if it doesn't exist then we obviously don't need to remove it.
        }
    }

    setScheduleFixedDayStartDate(fixedDayStartDate: FixedDayStartDate) {
        this.paymentStore.setScheduleFixedDayStartDate(fixedDayStartDate);
        this.setStartDate(AppDate.fromString(fixedDayStartDate.startDate.formattedFullDate, 'MM/dd/yyyy'));
    }

    /** Use this instead of setting directly */
    setStartDate(date: AppDate) {
        this.startDate = date;
        this.paymentStore.setStartDate(this.startDate);
        if (this.startDatePickerConfig) {
            this.startDatePickerConfig.defaultDate = this.startDate
        }
        if (this.startDate) {
            this.refreshEndDatePicker()
        }

        this.refreshEnd()
    }

    /** Use this instead of setting directly */
    setEndDate(date: AppDate, skipRefresh?: 'skip refresh') {
        const oldEndDate = this.endDate
        this.endDate = date;
        if (oldEndDate == null || !oldEndDate.equals(this.endDate)) {
            // set the input value if changed (prevents formatting itself)
            this.refreshEndDatePicker()
        }

        this.paymentStore.setEndDate(this.endDate);

        if (!skipRefresh) {
            this.refreshEnd()
        }
    }

    /** Use this instead of setting directly */
    setNumberOfPayments(numOfPmts: number, skipRefresh?: 'skip refresh') {
        if (numOfPmts && numOfPmts !== Number(this.numberOfPaymentsInputValue)) {
            // set the input value if changed (prevents formatting itself)
            this.numberOfPaymentsInputValue = String(numOfPmts)
        }

        this.numberOfPayments = (numOfPmts !== undefined ? numOfPmts : 1);
        this.paymentStore.setNumberOfPayments(this.numberOfPayments)

        if (!skipRefresh) {
            this.refreshEnd()
        }
    }

    refreshEnd() {
        if (!this.startDate) return

        if (this.endDate != null && this.startDate.isAfter(this.endDate)) {
            // start date after end date
            this.endDate = this.startDate
        }

        let rawEndDate: AppDate;
        if (this.isFixedDaySchedule())
            rawEndDate = PaymentComponent.calculateFixedDayEndDate(this.scheduleFrequency, AppDate.fromString(this.scheduleFixedDayStartDate.nonAdjustedStartDate.formattedFullDate, 'MM/dd/yyyy'), this.numberOfPayments, this.scheduleFixedDay);
        else
            rawEndDate = PaymentComponent.calculateEndDate(this.scheduleFrequency, this.startDate, this.numberOfPayments);

        if (!this.endDate || rawEndDate.index !== this.endDate.index) {
            let pmtDateValidation = this.paymentDateService.validatePaymentDate(
                rawEndDate,
                this.appDataService.appConfig.CUTOFF_HOUR_SCHEDULE,
                this.appDataService.appConfig.CUTOFF_MINUTE_SCHEDULE);
            let newEndDate = rawEndDate;
            while (pmtDateValidation !== PaymentDateValidation.OK) {
                if (pmtDateValidation === PaymentDateValidation.WEEKEND || pmtDateValidation === PaymentDateValidation.HOLIDAY) {
                    newEndDate = this.paymentDateService.getNextValidDateForPaymentSchedules(newEndDate);
                    pmtDateValidation = this.paymentDateService.validatePaymentDate(
                        newEndDate,
                        this.appDataService.appConfig.CUTOFF_HOUR_SCHEDULE,
                        this.appDataService.appConfig.CUTOFF_MINUTE_SCHEDULE);
                } else {
                    // should not enter here, but don't want an infinite loop if for unknown reason it does
                    newEndDate = null;
                    break;
                }
            }

            this.setEndDate(newEndDate, 'skip refresh')
        }

    }

    saveScheduleName() {

        this.scheduleNameError = !this.scheduleName || this.scheduleName.trim().length === 0;
        this.paymentStore.setScheduleName(this.scheduleName);
    }

    onChangeNumberOfPayments() {
        const numOfPayments = Number(this.numberOfPaymentsInputValue)
        if (numOfPayments > -1) {
            this.setNumberOfPayments(numOfPayments)

            this.refreshEnd()
        }
    }

    static calculateEndDate(frequency: string, startDate: AppDate, numOfPmts: number): AppDate {
        switch (frequency) {
            case "Annual":
                return startDate.withYear(startDate.year + numOfPmts - 1);
            case "Quarterly":
                return startDate.withMonth(startDate.month + (numOfPmts - 1) * 3);
            case "Semi-Annual":
                return startDate.withMonth(startDate.month + (numOfPmts - 1) * 6);
            case "Monthly": // fall through on monthly for default
            default:
                return startDate.withMonth(startDate.month + numOfPmts - 1);
        }
    }

    static calculateFixedDayEndDate(frequency: string, nonAdjustedStartDate: AppDate, numOfPmts: number, fixedDay: number): AppDate {
        let result = this.calculateEndDate(frequency, nonAdjustedStartDate, numOfPmts);
        let daysInMonth = new Date(result.year, result.month + 1, 0).getDate();
        // we don't want setting the date to roll into the next month
        return result.withDate(Math.min(fixedDay, daysInMonth));
    }

    updateScheduleFrequency() {
        this.paymentStore.setScheduleFrequency(this.scheduleFrequency);
        this.setEndDate(this.endDate);

        this.refreshEnd()
    }

    updatePaymentMethod() {
        this.paymentStore.setPaymentMethod(this.currentPaymentMethod);
        if (this.amountToPayInputValue && this.amountToPayInputValue.length > 0) {
            this.updateAmount();
        }
        if (this.router.url.endsWith('/payment')) {
            if (this.currentPaymentMethod.routingNumber) {
                this.router.navigate(['ach'], { relativeTo: this.route });
            } else {
                this.router.navigate(['card'], { relativeTo: this.route });
            }
        }
    }

    updateScheduleType() {
        this.paymentStore.setPaymentScheduleType(this.currentPaymentScheduleType);
        if (this.isFixedDaySchedule())
            this.updateScheduleFixedDayStartDate();
        else
            this.refreshDatePickers();
    }

    updateScheduleFixedDayStartDate() {
        this.setScheduleFixedDayStartDate(this.scheduleFixedDayStartDate);
    }

    getScheduleRanges() {
        this.scheduleRanges = {};
        let futurePayments = this.property.allFuturePayments;
        if (!futurePayments) return

        for (let futurePayment of futurePayments) {
            const pmtDate = AppDate.fromString(futurePayment.date.formattedFullDate, "MM/dd/yyyy")

            if (!this.scheduleRanges[futurePayment.correspondingPaymentScheduleNickname]) {
                //adding a new range
                this.scheduleRanges[futurePayment.correspondingPaymentScheduleNickname] = {
                    nickname: futurePayment.correspondingPaymentScheduleNickname,
                    startDate: pmtDate,
                    endDate: pmtDate,
                    futurePayment: futurePayment //might contain useful information for the popup (ids, amounts, description, etc)
                };
            } else {
                //I don't have to check if the date is actually greater than, because the list is already sorted
                this.scheduleRanges[futurePayment.correspondingPaymentScheduleNickname].endDate = pmtDate;
                // use next end date to extend the existing range
            }
        }
    }

    getCheckForOverlapEndDate(): Promise<AppDate> {
        return new Promise<AppDate>((resolve, reject) => {
            if (this.selectEndDate === "Enter Number of Payments") {
                resolve(this.endDate);
            } else {
                if (this.isFixedDaySchedule()) {
                    this.paymentScheduleApi.getFixedDayStartDates(this.scheduleFixedDay, this.MAX_PAYMENTS,
                        AppDate.fromString(this.scheduleFixedDayStartDate.nonAdjustedStartDate.formattedFullDate, "MM/dd/yyyy")
                            .format("yyyy-MM-dd"), this.scheduleFrequency).pipe(take(1))
                        .subscribe({ next: (resp) => {
                            resolve(AppDate.fromString(resp.data[resp.data.length - 1].startDate.formattedFullDate, "MM/dd/yyyy"));
                        }, error: () => {
                            reject();
                        } });
                } else {
                    this.paymentScheduleApi.getFuturePaymentDates(this.MAX_PAYMENTS, this.startDate.format("yyyy-MM-dd"), this.scheduleFrequency)
                        .pipe(take(1)).subscribe({ next: (resp) => {
                            resolve(AppDate.fromString(resp.data[resp.data.length - 1].formattedFullDate, "MM/dd/yyyy"));
                        }, error: () => {
                            reject();
                        } });
                }
            }
        })
    }

    checkForOverlap(): Promise<{ value: boolean }> {
        return new Promise<{ value: boolean }>((resolve, reject) => {

            this.getCheckForOverlapEndDate().then((endDate) => {
                if (this.numberOfPayments &&
                    this.paymentDateService.isFutureDate(this.startDate) &&
                    this.paymentDateService.isFutureDate(endDate) &&
                    this.isEndDateAfterStartDate(endDate)) {
                    let overlappingSchedules: PaymentScheduleRange[] = [];
                    let currentStart = this.startDate.index;
                    let currentEnd = endDate.index;
                    for (let key in this.scheduleRanges) {
                        const schedule = this.scheduleRanges[key]
                        // todo("Type these startDate and endDates correctly", schedule)
                        const rangeStart = schedule.startDate.index;
                        const rangeEnd = schedule.endDate.index;
                        if ((currentStart >= rangeStart && currentStart <= rangeEnd) || (rangeStart >= currentStart && rangeStart <= currentEnd)) {
                            overlappingSchedules.push(schedule);
                        }
                    }
                    if (overlappingSchedules.length > 0) {
                        const dialogRef = this.dialog.open(ScheduleOverlapDialog, {
                            data: { schedules: overlappingSchedules }
                        })
                        dialogRef.afterClosed().pipe(take(1)).subscribe(value => {
                            resolve({ value: !!value });
                        })
                    } else {
                        resolve({ value: true });
                    }
                }
            }, () => {
                resolve({ value: true })
            });
        })
    }

    setPaymentScheduleOptions() {

        try {
            this.contactApi.productDetailsPOST(
                false,
                this.property.hoa.pmc.couponId,
                this.property.hoa.couponId,
            ).pipe(take(1)).subscribe((data) => {
                if (data != null) {
                    this.paymentScheduleOptions = [
                        { display: 'Fixed Amount', value: StatusType.FIXED_AMOUNT }
                    ]
                    let autopay = data.products.find(product => product.productType === 'autopay');
                    if (autopay != null && autopay.productStatus === 'active') {
                        this.paymentScheduleOptions.push({
                            display: 'Account Balance',
                            value: StatusType.ACCOUNT_BALANCE
                        });
                        this.setFixedDayScheduleOptions(autopay);
                    }
                    this.setScheduleFrequencyOptions(data.products.find(product => product.productType === 'configure-schedule-frequency'));
                } else {
                    this.dialog.open(DialogErrorDialog, {
                        data: { error: 'Error trying to create payment schedule.' }
                    })
                }
            });
        } catch(err) {
            this.networkErrorService.handleNetworkError(err, true)
                .pipe(take(1)).subscribe((shouldRetry) => {
                    if (shouldRetry === true) {
                        this.setPaymentScheduleOptions();
                    }
                });
        }
    }

    setScheduleFrequencyOptions(configureScheduleFrequency: Product) {
        if (configureScheduleFrequency && configureScheduleFrequency.productStatus === 'active') {
            this.setScheduleFrequencyOption(configureScheduleFrequency, 'Monthly');
            this.setScheduleFrequencyOption(configureScheduleFrequency, 'Quarterly');
            this.setScheduleFrequencyOption(configureScheduleFrequency, 'Semi-Annual');
            this.setScheduleFrequencyOption(configureScheduleFrequency, 'Annual');
        } else {
            this.scheduleFrequencies = [ 'Monthly', 'Quarterly', 'Semi-Annual', 'Annual' ]
        }
        this.scheduleFrequency = this.scheduleFrequencies[0];
    }

    setScheduleFrequencyOption(configureScheduleFrequency: Product, name: string) {
        let freqAttribute = configureScheduleFrequency.productAttributes.find(attribute => attribute.name === name.toLowerCase())
        if (!freqAttribute || (freqAttribute && freqAttribute.value === 'active'))
            this.scheduleFrequencies.push(name)
    }

    setFixedDayScheduleOptions(autopay: Product) {
        let dayOfMonthAttribute = autopay.productAttributes.find(attribute => attribute.name === 'day-of-month');
        if (dayOfMonthAttribute != null) {
            this.scheduleFixedDay = new Number(dayOfMonthAttribute.value).valueOf();
            this.paymentScheduleApi.getFixedDayStartDates(this.scheduleFixedDay)
                .pipe(take(1)).subscribe({
                    next: (resp) => {
                        if (resp.data && resp.data) {
                            this.scheduleFixedDayStartDates = resp.data;
                            this.scheduleFixedDayStartDate = resp.data[0];
                        }
                    },
                    error: error => {
                        console.error("paymentScheduleApi.getFixedDayStartDates Error");
                        console.error(error);
                    } })
        }
    }

    isFixedDaySchedule() {
        return this.uiManipulation === PaymentUiState.SCHEDULE_PAYMENT &&
            this.currentPaymentScheduleType === StatusType.ACCOUNT_BALANCE &&
            this.scheduleFixedDayStartDates != null;
    }

    scheduleNameHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Schedule Name",
                "message": "e.g., Monthly Assessments",
                "buttonText": "Close"
            }
        })
    }

    scheduleTypeHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Schedule Type",
                "message": "By selecting Account Balance, your payment is the amount due on your account. The amount due and your payment amount may vary each month and may include administrative fees. The amount charged may vary due to an increase in assessments or other charges from your management company. Your payment will not exceed the charges from your management company plus any applicable administrative fees.",
                "buttonText": "Close"
            }
        })
    }

    startDateHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Start Date",
                "message": "These are the available start dates based on your Management Company's payment cycle. Payments are processed Monday through Friday, excluding holidays.",
                "buttonText": "Close"
            }
        })
    }

    updateCartItemsTotal() {
        let tempTotal = 0;
        this.cartItems.forEach(cartItem => {
            tempTotal += cartItem.qty * cartItem.amount;
        })
        this.amountToPayInputValue = moneyNumber(tempTotal).toString();
        this.amountToPay = Number(this.amountToPayInputValue);
        if (tempTotal > this.maxPrincipalAmount) {
            let maxPrincipalAmt = new Intl.NumberFormat( 'en-US', { currency : 'USD' }).format(this.maxPrincipalAmount);
            this.amenityTotalErrorMsg = 'Order Total cannot exceed $' + maxPrincipalAmt;
        } else {
        	this.amenityTotalErrorMsg = undefined;
        }
    }

    validateCartItems() {
        this.refreshAmenities().then(() => {
            this.amenityService.validateCartItems(this.availableAmenities, this.cartItems);
        });
    }

    refreshAmenities(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.amenityApi.activeAmenitiesByCouponGet(false, this.property.hoa.pmc.couponId, this.property.hoa.couponId, this.property.couponId)
                .pipe(take(1)).subscribe({ next: (response) => {
                    this.availableAmenities = response;
                    this.availableCategories = this.amenityService.getAvailableCategories(this.availableAmenities);
                    resolve();
                }, error: (err) => {
                    let errMsg: string = undefined;
                    if (err.error && err.error.message) {
                        errMsg = err.error.message;
                    }
                    this.errorHandlerService.handleNetworkError(err, true, errMsg)
                        .pipe(take(1)).subscribe({ next: () => {}, error: () => {} });
                    reject();
                } });
        });
    }

    setPaymentOptions() {
        //if was not already sent from the previous page
        if (!this.availableAmenities) {
            this.refreshAmenities().then(() => {}, () => {});
        } else {
            this.availableCategories = this.amenityService.getAvailableCategories(this.availableAmenities);
        }
    }

    startDateChange(value: unknown) {
        if(value instanceof AppDate) {
            this.setStartDate(value)
            this.startDateError = null
        } else if ((typeof value) === 'string' ) {
            this.startDateError = value as string;
        }
    }

    endDateChange(value: unknown){
        if(value instanceof AppDate) {
            this.endDateError = null
            // clear error
            if (value.equals(this.endDate)) {
                // no change and it's determined by number of payments
                return
            }

            this.setEndDate(value)
        } else if ((typeof value) === 'string' ) {
            this.endDateError = value as string
        }
    }

    addNewCartItem() {
        this.validateCartItems();
        this.updateCartItemsTotal();
        this.updateAmount();
    }

    updateCartItem(){
        this.validateCartItems();
        this.updateCartItemsTotal();
        this.updateAmount();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
